import type { CredentialResponse } from "@react-oauth/google";
import type { WorkspaceDefaultProjectType } from "schemas/generated/workspaceDefaultProjectType";

import * as React from "react";

import { useNextUrlWithPendingAppInstallationId } from "@v2/features/account/common";
import { authRouter } from "@v2/trpc/routes/auth";
import { storeToken } from "@v2/utils/store-token";
import some from "lodash-es/some";
import { useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { isLocalhost } from "replo-runtime/shared/env";
import { isEmpty } from "replo-utils/lib/misc";
import { z } from "zod";

const emailSchema = z.email();
const passwordSchema = z
  .string()
  .min(8, "Passwords must contain at least 8 characters.")
  .max(100, "Passwords must contain at most 100 characters.");

type SignupFormValues = {
  email: string;
  password: string;
};

export const useSignupForm = ({
  defaultProjectType,
}: { defaultProjectType?: WorkspaceDefaultProjectType } = {}) => {
  const [searchParams] = useSearchParams();

  const { nextUrl, nextSearchParams } =
    useNextUrlWithPendingAppInstallationId();

  const defaultProjectTypeInput = defaultProjectType ?? "shopify_theme_builder";

  // NOTE (Yuxin, 2026-07-18): All funnels navigate to nextUrl ?? "/"; the App gate routes zero-project agent users to onboarding.
  const handleAccountCreation = React.useCallback(
    (token: string) => {
      storeToken(token);
      window.location.replace(
        `${nextUrl ?? "/"}${nextSearchParams ? `?${nextSearchParams}` : ""}`,
      );
    },
    [nextUrl, nextSearchParams],
  );

  // The funnel travels as the workspace's defaultProjectType on one shared route
  // pair, so the surface a user came from picks the funnel — no separate routes.
  const googleAccountMutation = authRouter.createAccountWithGoogle.useMutation({
    onSuccess: ({ token }) => handleAccountCreation(token),
  });
  const { mutate: createGoogleAccountApi } = googleAccountMutation;

  const accountMutation = authRouter.createAccount.useMutation({
    onSuccess: ({ token }) => handleAccountCreation(token),
  });
  const { mutate: createAccountApi } = accountMutation;

  const isLoadingCreateAccount = accountMutation.isPending;

  // Note (Kevin, 2026-01-03, REPL-21631): Prefill test credentials on localhost
  const shouldPrefillTestCredentials = isLocalhost();

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    control,
  } = useForm<SignupFormValues>({
    defaultValues: {
      email: shouldPrefillTestCredentials
        ? "test@replo.app"
        : searchParams.get("email") ?? "",
      password: shouldPrefillTestCredentials ? "12345678" : "",
    },
    mode: "onBlur",
  });

  const watchAllFields = useWatch({ control });
  const someFieldIsEmpty = some(Object.values(watchAllFields), isEmpty);

  const onGoogleSubmit = React.useCallback(
    async (credentialResponse: CredentialResponse) => {
      const { clientId, credential } = credentialResponse;
      if (clientId && credential) {
        const pendingTemplateId = sessionStorage.getItem(
          "replo_pending_template_id",
        );
        createGoogleAccountApi({
          clientId,
          credential,
          installationNonce: searchParams.get("nonce"),
          defaultProjectType: defaultProjectTypeInput,
          ...(pendingTemplateId ? { pendingTemplateId } : {}),
        });
      }
    },
    [createGoogleAccountApi, searchParams, defaultProjectTypeInput],
  );

  const onSubmit = React.useCallback(
    async ({ email, password }: SignupFormValues) => {
      const pendingTemplateId = sessionStorage.getItem(
        "replo_pending_template_id",
      );
      createAccountApi({
        email,
        password,
        installationNonce: searchParams.get("nonce"),
        defaultProjectType: defaultProjectTypeInput,
        ...(pendingTemplateId ? { pendingTemplateId } : {}),
      });
    },
    [createAccountApi, searchParams, defaultProjectTypeInput],
  );

  const emailError = !isEmpty(watchAllFields.email)
    ? errors?.email?.message
    : undefined;

  const passwordError = !isEmpty(watchAllFields.password)
    ? errors?.password?.message
    : undefined;

  const buttonShouldBeDisabled =
    someFieldIsEmpty ||
    Boolean(passwordError) ||
    Boolean(emailError) ||
    isLoadingCreateAccount;

  const displayErrorMessage =
    googleAccountMutation.error?.message ??
    accountMutation.error?.message ??
    null;

  const emailRegisterProps = register("email", {
    required: "Please enter a valid email address.",
    validate: (value) => {
      const parsedEmail = emailSchema.safeParse(value);
      if (parsedEmail.success) {
        return true;
      }
      return parsedEmail.error?.issues[0]?.message;
    },
    onChange: () => clearErrors("email"),
  });

  const passwordRegisterProps = register("password", {
    required: "Please enter a valid password.",
    validate: (value) => {
      const parsedPassword = passwordSchema.safeParse(value);
      if (parsedPassword.success) {
        return true;
      }
      return parsedPassword.error?.issues[0]?.message;
    },
    onChange: () => clearErrors("password"),
  });

  return {
    register,
    emailRegisterProps,
    passwordRegisterProps,
    handleSubmit,
    onSubmit,
    onGoogleSubmit,
    errors,
    emailError,
    passwordError,
    isLoadingCreateAccount,
    buttonShouldBeDisabled,
    displayErrorMessage,
    control,
  };
};
